import React from "react";
import Classes, { CheckingAccount } from "model";
import StopPayment from "components/workflows/StopPaymentsWorkflow/classes/StopPayment";
import { BCMSCurrency, Branding,  useBCMSContext, BCMSStateManagerCRUD, useWorkflow, useStateManager, Link, BCMSRecord, BrandingStyle, useModel, BCMSValidate } from "bcms-react";
import DateSelect from 'components/custom-ui/DateSelect';
import CustomSelect, { CustomSelectItem } from 'components/custom-ui/CustomSelect';
import UIRadioGroup, { UIRadio } from 'components/custom-ui/UIRadioGroup';
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from 'components/shared';
import { PrimaryButton, UIAmountInput, UITextInput } from 'components/ui';

import "./styles.css";
import CustomContentHeaderWidget from "components/shared/views/CustomContentHeaderWidget";

/**
 * @function StopPaymentPage
 * @return {React.Component}
 * @exported true
 * @description The main component for StopPaymentPage
 */
export default function StopPaymentPage(props) {
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);
    const { branding } = useModel();
    const { options } = branding;
    const data = { ...getDefaultPageData(options, globalController), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    const { nextStep, state: workflow } = useWorkflow() ?? {};

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "payment", prop ], value)
            .submit(setState);
        },

        onCustomFieldChange: (fieldID, fieldType) => value => {
          var updatePaymentProp = undefined;
          var updatePaymentPropValue = undefined;

          const fields = state.data.payment[fieldType].map(field => {

            if(field.id === fieldID) {
              const fieldType = field.fieldType;

              if(fieldType.toLowerCase().includes("check amount")) {
                updatePaymentProp = "amount";
                updatePaymentPropValue = value;
              } else if(fieldType.toLowerCase().includes("check number")) {
                updatePaymentProp = "checkNumber";
                updatePaymentPropValue = value;
              }

              field.enteredValue = value;

            }
            return field;
          });

          BCMSStateManagerCRUD.init(state)
          .update([ "data", "payment", updatePaymentProp ], updatePaymentPropValue)
          .update([ "data", "payment", fieldType ], fields)
          .submit(setState);
      },

        onSubmit: evt => {
            const payment = state.data.payment;
            payment.setDefaults(globalController.state.branding);

            const data = BCMSStateManagerCRUD.init(workflow)
                .update([ "data", "payment" ], payment)
                .submit();

            nextStep(data);
        }
    });

    return (
        <PageContainer className="stop-payment-page" state={ { data, ui } } controller={ controller }>
            <StopPaymentContent />
        </PageContainer>
    );
}

export const getDefaultPageData = (brandingOptions, controller) => {
  let options = brandingOptions ?? {};
  let stopPaymentFeeAmount = options.stop_payment_fee_amount_abi22;
  stopPaymentFeeAmount = stopPaymentFeeAmount === "undefined" ? "X.XX" : stopPaymentFeeAmount;
  stopPaymentFeeAmount = BCMSValidate.isNumber(stopPaymentFeeAmount)
  ? BCMSCurrency.init(stopPaymentFeeAmount).format()
  : stopPaymentFeeAmount;

  const stopCheckCustomFields = StopPayment.getArrayOfCustomFieldObjectsByBrandingOptionType(controller.state.branding, `stop_check_field`);
  const stopRangeCustomFields = StopPayment.getArrayOfCustomFieldObjectsByBrandingOptionType(controller.state.branding, `stop_range_field`);
  const payment = StopPayment.init({ stopCheckCustomFields, stopRangeCustomFields });
  const hasRangeOfChecks = options.range_of_checks_abi22 !== "false";

  return {
    hasRangeOfChecks,
    payment: payment,
    stopPaymentFeeAmount: stopPaymentFeeAmount
  }
};
export const getDefaultPageUI = () => ({
  accountDropdown: { isActive: false },
  reasonDropdown: { isActive: false }
});

function StopPaymentContent(props) {
    const { data, ui, onChange, onCustomFieldChange, onSubmit } = useStateManager();
    const stopPayment = data.payment;
    const { branding } = useModel();

    const isSinglePayment = stopPayment.paymentType === "Single Check";
    const isRangePayment = stopPayment.paymentType === "Range of Checks";
    const feeMessage = `A fee of ${ data.stopPaymentFeeAmount } applies for stop payment.`;

    // START: Build an array of custom fields set through branding options.
    const singleCheckFields = getFieldsByType("stopCheckCustomFields", stopPayment, onCustomFieldChange, ui);
    const rangeOfChecksFields = getFieldsByType("stopRangeCustomFields", stopPayment, onCustomFieldChange, ui);
    // END: Build an array of custom fields set through branding options.

    const subHeaderContent = branding.options.stop_payments_subheader_abi22;
    const promoText = branding.options.stop_payment_promo_abi22;

    return (
        <PageContentView>
          <div className="flex-container flex-vertical page-widget-wrapper">
            {promoText && <CustomContentHeaderWidget content = {promoText}/>}
            <Branding id="stop_payment_fee_abi22" value="true" default="false">
              <BrandingStyle style={{ "color" : "stop_payment_fee_text_abi22" }} className="stop-payment-fee" element={ <p>{ feeMessage }</p> } />          
            </Branding>

            <CenterContentContainer>
              <ContentHeaderView text="Stop Payments" data-point="Stop_Payment"/>
                {subHeaderContent && <CustomContentHeaderWidget content = {subHeaderContent}/>}
                <Link className="stop-payment-history-link" to="/stopPaymentHistory" dataPoint="Stop_Payment_History">Stop Payment History</Link>
                <FormContainer className="small-form-width">
                  <FormRowView text="Stop Payment Account:">
                    {/* AccountSelect */}
                    <AccountSelect dataPoint="Account_DD"  onChange={ onChange("account") } value={stopPayment.account} />
                  </FormRowView>
                  <FormRowView text="">
                    {/* UIRadios */}
                    <UIRadioGroup label="Stop Payment Type:" value={stopPayment.paymentType} onChange={onChange("paymentType")} >
                      <UIRadio name="stop-payment-type" label="Single Check" dataPoint="Single_Check" />
                      { data.hasRangeOfChecks && <UIRadio name="stop-payment-type" label="Range of Checks" dataPoint="Range_Check"  /> }
                    </UIRadioGroup>
                  </FormRowView>
                  { isSinglePayment && <> 
                  { singleCheckFields }
                  </>}
                  { isRangePayment && <>
                    <FormRowView text="Starting Check Number:">
                      <UITextInput onChange={onChange("startCheckNumber")} value={stopPayment.startCheckNumber} data-point="Start_Check_Number_Input" />
                    </FormRowView>
                    <FormRowView text="Ending Check Number:">
                      <UITextInput onChange={onChange("endCheckNumber")} value={stopPayment.endCheckNumber} ata-point="End_Check_Number_Input"/>
                    </FormRowView>
                    { rangeOfChecksFields }
                  </>}
                  
                  <FormRowView>
                    <PrimaryButton text="Submit" data-point="Submit" onClick={ onSubmit } />
                  </FormRowView>
                </FormContainer>
              </CenterContentContainer>
            </div>
          </PageContentView>
    )
}

export function AccountSelect(props){
    const { ui } = useStateManager() ?? {};
    const isActive = ui !== undefined && ui.accountDropdown ? ui.accountDropdown.isActive : false;
    const accounts = CheckingAccount.initAll();
    const value = props.value === undefined || props.value === "" ? accounts[0] : props.value;

    let accountItems = accounts.map(function(account){
        return  <CustomSelectItem value={account} key={account.id} data-point={`Checking_Account_${account.id}`}>
                    <p>{account.number} - {account.nickname} (Available {account.availableBalance.format()})</p>
                </CustomSelectItem>
    });

    const dataPoint = props.dataPoint ?? "";

    return(
        <CustomSelect active={ isActive } value={ value } data-point={dataPoint} onChange={ props.onChange }>
            {accountItems}
        </CustomSelect>
    )
}

/**
 * @function getFieldsByType
 * @param {STRING} type - The type of custom fields needed, either "stopCheckCustomFields" or "stopRangeCustomFields".
 * @param {StopPayment} stopPayment - The instance of StopPayment in the page.
 * @param {Function} onCustomFieldChange - The instance of onCustomFieldChange in the state.
 * @param {OBJECT} ui - The instance of ui in the state.
 * @return {ARRAY} - Returns an array of custom fields deteremined by branding options.
 * @exported false
 * @description Returns an array of custom fields deteremined by branding options.
 */
function getFieldsByType(type, stopPayment, onCustomFieldChange, ui) {
  return stopPayment[type].reduce((acc, field) => {
    const fieldID = field.id;
    const fieldTypeFormatted = field.fieldType;
    const fieldType = fieldTypeFormatted.toLowerCase();
    const text = `${ fieldTypeFormatted }:`;

    if(fieldType.includes(`check number`)) {
      acc.push(
        <FormRowView key={ fieldID } text={ text }>
          <UITextInput data-point={ `Check_Number_Input` } onChange={ onCustomFieldChange(fieldID, type) }value={ field.enteredValue } />
        </FormRowView>
      );
    } else if(fieldType.includes(`payee`)) {
      acc.push(
        <FormRowView key={ fieldID } text={ text }>
          <UITextInput data-point={ `Payee_Input` } onChange={ onCustomFieldChange(fieldID, type) }value={ field.enteredValue } />
        </FormRowView>
      );
    } else if(fieldType.includes(`check date`)) {
      acc.push(
        <FormRowView key={ fieldID } text={ text } data-point="Transfers_Send_On">
            <DateSelect data-point={ `Check_Date_Input` } onChange={ onCustomFieldChange(fieldID, type) } />
        </FormRowView>
      );
    } else if(fieldType.includes(`reason`)) {
      acc.push(
      <FormRowView key={ fieldID } text={ text }>
        <CustomSelect active={ ui.reasonDropdown.isActive } value={ field.enteredValue } data-point={ `Reason_DD` } onChange={ onCustomFieldChange(fieldID, type) }>
          <CustomSelectItem data-point="Select_A_Reason">--Select a Reason--</CustomSelectItem>
          <CustomSelectItem data-point="Incorrect_Amount">Check Issued for Incorrect Amount</CustomSelectItem>
          <CustomSelectItem data-point="Lost_Check">Lost Check</CustomSelectItem>
          <CustomSelectItem data-point="Stale_Check">Stale Check</CustomSelectItem>
          <CustomSelectItem data-point="Stolen_Check">Stolen Check</CustomSelectItem>
          <CustomSelectItem data-point="Other">Other</CustomSelectItem>
        </CustomSelect>
      </FormRowView>
      );
    } else if(fieldType.includes(`check amount`)) {
      acc.push(
      <FormRowView key={ fieldID } text={ text }>
          <UIAmountInput data-point={ `Check_Amount_Input` } onChange={ onCustomFieldChange(fieldID, type) } value={ field.enteredValue ? field.enteredValue.amount : field.enteredValue } />
      </FormRowView>
      );
    }
    return acc;
  }, []);
}